import { Link } from 'react-router-dom';
import LearnPage from './LearnPage';

const FAQS = [
  {
    q: 'What is an ADR?',
    a: 'An American Depositary Receipt is a US-listed certificate that represents shares of a foreign company. A US bank (the depositary) holds the underlying foreign shares and issues ADRs that trade on US exchanges in US dollars and pay dividends in US dollars. From your brokerage screen, it looks and feels like a regular stock. ASML, TSM, NVO, UL, SAP, BTI, AZN, TM and HMC are all ADRs.',
  },
  {
    q: 'What is dividend withholding tax?',
    a: 'When a foreign company pays a dividend to a US investor, the home country usually takes a percentage off the top before the cash ever reaches your brokerage. The rate depends on the country and whether a US tax treaty applies. UK is 0%, Canada 15% (treaty, and 0% inside an IRA), Switzerland 35% withheld with a 15% treaty rate (20% reclaimable), Germany 26.375% withheld / 15% treaty, Japan 15.315% withheld / 10% treaty, Australia 30% on unfranked dividends / 15% treaty, France 25% withheld / 15% treaty after paperwork, Spain 19% withheld / 15% treaty.',
  },
  {
    q: 'Can I get foreign withholding tax back?',
    a: 'Partially — through the Foreign Tax Credit on IRS Form 1116. For most US investors, total foreign tax under $300 ($600 if married filing jointly) can be claimed directly on Schedule 3 without filing Form 1116. Above that threshold, Form 1116 is required and the credit is capped by your US tax liability on that foreign income. A few high-withholding countries (Switzerland, Germany, France) require separate reclaim paperwork to bring the rate down to treaty levels — most retail investors leave that money on the table.',
  },
  {
    q: 'Why are foreign dividends bad in IRAs?',
    a: 'Counterintuitive but real: the Foreign Tax Credit only works against US tax owed. Inside a traditional IRA or Roth IRA, you owe no US tax on the dividend in the year received, so you cannot claim the credit — the foreign withholding becomes a permanent leak. Hold ADRs from high-withholding countries (Switzerland, Germany, France, Spain) in a taxable account where the FTC actually offsets US tax. Hold UK ADRs (0% withholding) and Brazilian ADRs (0% on dividends) anywhere.',
  },
  {
    q: 'Are ADRs qualified dividends?',
    a: 'Usually yes, if (a) the underlying company is incorporated in a country with a comprehensive US tax treaty, and (b) you meet the 61-day holding period rule. Most major ADRs (Europe, Canada, Japan, Australia, Taiwan, Mexico) qualify. A few do not — notably ADRs from countries without a comprehensive treaty, and certain pass-through structures. Your 1099-DIV from the broker will split qualified vs ordinary for you.',
  },
  {
    q: 'Are there ADR custodial fees?',
    a: 'Yes. Most ADRs charge a small "ADR pass-through fee" — typically $0.01-$0.05 per share per year — collected by the depositary bank for administering the program. The fee is netted from one of your dividends each year and shows up on the 1099-DIV as a separate line. On a $50 ADR yielding 3%, a $0.02/share fee is roughly 1-2% of the annual dividend. Not enormous, but worth being aware of for high-yield holdings.',
  },
];

export default function LearnInternationalDividends() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  return (
    <LearnPage
      title="International Dividends — ADRs, Withholding Tax & The Foreign Tax Credit (2026) | DiviDrip"
      description="A US investor's guide to international dividend investing. How ADRs work, country-by-country withholding rates, the Foreign Tax Credit, and why foreign dividends belong in taxable accounts — not IRAs."
      canonical="https://twylightcrow.com/learn/international-dividends"
      guidePath="/learn/international-dividends"
      sectionLabel="Vehicles & Structures"
      sectionAnchor="vehicles"
      kicker="Learn · International investing"
      h1="International Dividends — ADRs, Withholding Tax & The Foreign Tax Credit"
      datePublished="2026-05-23"
      jsonLd={jsonLd}
      related={[
        { to: '/learn/qualified-vs-ordinary-dividends', label: 'Qualified vs ordinary dividends — 2026 tax guide' },
        { to: '/learn/mlps-explained', label: 'MLPs explained — K-1 forms and IRA traps' },
        { to: '/learn/why-drip-roth', label: 'Why DRIP in my Roth but not in my taxable' },
        { to: '/learn/first-10-tickers', label: 'The first 10 tickers — a starter portfolio' },
        { to: '/glossary#entry-foreign-tax-credit-ftc-form-1116', label: 'Glossary: Foreign Tax Credit (FTC) / Form 1116' },
        { to: '/glossary#entry-foreign-withholding-tax-wht', label: 'Glossary: Foreign Withholding Tax (WHT)' },
        { to: '/glossary#entry-adr-tax-drag', label: 'Glossary: ADR Tax Drag' },
        { to: '/glossary', label: 'Glossary — every dividend term defined' },
      ]}
    >
      <p>
        Most US dividend investors stick to S&P 500 names and never look past the
        border. They're leaving an enormous chunk of the global dividend universe
        on the table — Nestlé, Novartis, Unilever, Taiwan Semiconductor, ASML, BHP,
        Shell, AstraZeneca. Companies with 30+ year dividend streaks, fortress
        balance sheets, and yields that rival anything in the US.
      </p>
      <p>
        The reason most US investors skip them isn't the companies — it's the tax
        plumbing. Foreign dividends arrive with a chunk already withheld by the
        home country, and depending on where you hold the position, you may or may
        not get that money back. Here's the full picture as of the{' '}
        <strong>2026 tax year</strong>.
      </p>

      <h2>How ADRs actually work</h2>
      <p>
        Almost every foreign stock a US investor buys on a US brokerage is an{' '}
        <strong>American Depositary Receipt</strong>. A US bank — typically BNY
        Mellon, Citi, JPMorgan, or Deutsche Bank — buys the underlying foreign
        shares, holds them in a custody account overseas, and issues ADRs that
        trade on US exchanges in US dollars.
      </p>
      <p>
        Practically, that means:
      </p>
      <ul>
        <li>You buy and sell in US dollars on the NYSE or Nasdaq during US market hours.</li>
        <li>Dividends are converted to USD by the depositary bank before they hit your account.</li>
        <li>You get a standard 1099-DIV at year-end, with foreign tax withheld broken out separately.</li>
        <li>You never deal with foreign brokerages, foreign currency accounts, or foreign tax filings directly.</li>
      </ul>
      <p>
        ADRs come in tiers (Level I, II, III) that determine how much SEC reporting
        the foreign company has signed up for. For dividend investors, the
        difference rarely matters — what matters is the country, the yield, and
        the withholding rate.
      </p>

      <h2>The 2026 withholding tax cheat sheet (US treaty rates)</h2>
      <p>
        Every country sets its own dividend withholding rate. The US has tax
        treaties with most major economies that reduce that rate for US
        residents — provided your broker has filed the right paperwork (a W-8BEN
        or its equivalent, which Schwab, Fidelity, IBKR etc. handle automatically).
        The rates below are the <em>treaty</em> rates that should apply to a normal
        US retail brokerage account in 2026:
      </p>
      <ul>
        <li><strong>United Kingdom — 0%.</strong> The UK is a unicorn. No withholding on dividends regardless of account type. UL, BTI, GSK, AZN, BP, RIO, SHEL all pay you 100% of declared dividends.</li>
        <li><strong>Brazil — 0%.</strong> Brazil currently exempts dividends from withholding (a tax reform changing this has been debated for years but has not passed as of early 2026). VALE, PBR, ITUB pay clean.</li>
        <li><strong>Hong Kong — 0%.</strong> No dividend withholding tax on most HK-domiciled issuers.</li>
        <li><strong>Mexico — 10%.</strong> Treaty rate. AMX, CX, FMX.</li>
        <li><strong>Japan — 10%.</strong> Treaty rate for US residents (the statutory rate is 15.315% — 15% national plus a 0.315% reconstruction surcharge — so the gap is reclaimable). TM, HMC, MUFG, SMFG, SONY.</li>
        <li><strong>Taiwan — 21%.</strong> No comprehensive treaty with the US, so the full statutory rate applies. TSM holders eat 21%.</li>
        <li><strong>Canada — 15%.</strong> Treaty rate (statutory is 25%). RY, TD, BNS, ENB, BCE, MFC. Special rule: 0% withholding on Canadian dividends held in an IRA, thanks to the US–Canada treaty — Canada is the one exception to the "no foreign dividends in IRAs" rule.</li>
        <li><strong>Netherlands — 15%.</strong> Treaty rate. ASML, RDSA→SHEL (now UK-domiciled), HEIA.</li>
        <li><strong>Australia — 15% (treaty).</strong> Note: most Australian dividends are "franked" — partially or fully credited for company tax already paid — and franked portions are technically exempt from withholding. The unfranked portion gets 15%. BHP, RIO (Australian listing), WBK.</li>
        <li><strong>Spain — 19% withheld, 15% treaty rate.</strong> The 2019 US–Spain protocol cut portfolio dividends to 15%, so the 4% gap above the 19% domestic rate is reclaimable (and 0% applies to large corporate holders). SAN, TEF, IBE.</li>
        <li><strong>France — 25% withheld, 15% treaty rate.</strong> The 10% gap is reclaimable via Form 5000/5001 — almost no retail investor bothers. LVMHF, TTE, SU.</li>
        <li><strong>Germany — 26.375% withheld, 15% treaty rate.</strong> Same story as France: an 11.375% gap is reclaimable in theory, near-impossible in practice for retail. SAP, BAYRY, SIEGY.</li>
        <li><strong>Switzerland — 35% withheld, 15% treaty rate.</strong> The worst on this list. Switzerland withholds the full 35% statutory rate up front and forces you to reclaim 20% via <strong>Form 82</strong> — paperwork that takes 6-18 months and which most US retail investors simply abandon. Some brokers (Fidelity, Schwab, Interactive Brokers) now apply the 15% treaty rate at source, so ask before you buy. NVS, NSRGY, ROG.</li>
      </ul>
      <p>
        The countries where the headline withholding number is much higher than the
        treaty rate (Switzerland, Germany, France) are where a lot of US investors
        get unpleasant surprises.
      </p>

      <h2>The Foreign Tax Credit — how you actually get the money back</h2>
      <p>
        For dividends held in a <strong>taxable account</strong>, the foreign tax
        withheld is not gone — it becomes a credit against your US tax bill via
        the <strong>Foreign Tax Credit (FTC)</strong>.
      </p>
      <p>
        The mechanics are friendlier than they sound:
      </p>
      <ul>
        <li><strong>Under $300 single / $600 joint</strong> of total foreign tax withheld — claim it directly on Schedule 3 of your 1040. No additional forms required.</li>
        <li><strong>Above those thresholds</strong> — file Form 1116, which computes a cap on the credit based on the ratio of foreign-source income to total income. Most filers under $1,000 of foreign tax see no real cap effect.</li>
        <li>Tax software (TurboTax, H&R Block, FreeTaxUSA) handles both paths automatically once you import the 1099-DIV.</li>
        <li>Unused FTC can be carried back 1 year or forward 10 years.</li>
      </ul>
      <p>
        Net result for a typical US investor with $20,000 in ADRs yielding 3%, half
        Canadian / half UK: ~$300 in Canadian dividends with $45 withheld → claim
        $45 back via FTC on Schedule 3. UK dividends are clean. Effective foreign
        tax drag: zero.
      </p>

      <h2>The IRA trap — and the one exception</h2>
      <p>
        Here is the rule that surprises everyone: <strong>the Foreign Tax Credit
        only offsets US tax you actually owe in the year the dividend is
        received.</strong> Inside a traditional IRA or Roth IRA, you owe zero US
        tax on dividends in the year received. So there is no US tax for the
        credit to offset. The foreign tax withheld is gone — permanently.
      </p>
      <p>
        Hold a Swiss ADR like Nestlé (NSRGY) inside a Roth IRA and Switzerland
        keeps 35% of every dividend, forever, with no recourse. Hold the same
        position in a taxable account and the credit recovers 15-35% of that.
      </p>
      <p>
        <strong>The Canada exception.</strong> The US–Canada tax treaty specifically
        carves out an exemption for dividends paid into US retirement accounts.
        Canadian dividends held in an IRA (or 401(k)) face <strong>0%
        withholding</strong>. Hold RY, TD, BNS, ENB inside your Roth without
        penalty — they're the only foreign dividends that get the IRA treatment
        Americans assume applies broadly.
      </p>

      <h2>Account-placement playbook for international dividends</h2>
      <ul>
        <li><strong>Taxable brokerage:</strong> Best home for high-withholding countries — Switzerland, Germany, France, Netherlands, Spain, Australia, Japan. The FTC reclaims most of the friction.</li>
        <li><strong>Roth IRA:</strong> UK ADRs (0% withholding) and Canadian ADRs (0% under treaty) only. Avoid everything else.</li>
        <li><strong>Traditional IRA / 401(k):</strong> Same as Roth — UK + Canada only. The deferred US tax doesn't help against foreign withholding.</li>
        <li><strong>HSA:</strong> Same logic as Roth. UK and Canada.</li>
      </ul>

      <h2>ADR custodial fees — the small leak nobody mentions</h2>
      <p>
        ADRs charge a small custodial fee — usually $0.01-$0.05 per share per year
        — to cover the depositary bank's costs. The fee is netted out of one of
        your dividend payments each year (typically the one nearest the program's
        anniversary date). On a 100-share position in a $50 ADR yielding 3%, a
        $0.02 fee is $2 against $150 of annual dividends — about 1.3% drag.
      </p>
      <p>
        Worth noting for high-yield ADRs but not a deal-breaker. The fee shows
        up on the 1099-DIV under "non-dividend distributions" or as a separate
        adjustment line.
      </p>

      <h2>Currency risk — usually overstated</h2>
      <p>
        Yes, the dividend gets converted from EUR/CHF/JPY/etc. into USD before it
        lands in your account, and that conversion happens at whatever spot rate
        applies. Over a 30-year holding period, currency fluctuations roughly
        cancel out for diversified portfolios of dividend-paying multinationals
        (most of these companies earn revenue in multiple currencies anyway).
      </p>
      <p>
        Don't avoid international dividends because of FX risk. Avoid them, or
        don't, based on the tax and account-placement logic above.
      </p>

      <h2>FAQ</h2>
      <dl className="learn-faq">
        {FAQS.map(({ q, a }) => (
          <div key={q} className="my-4">
            <dt className="font-semibold text-foreground">{q}</dt>
            <dd className="mt-1 text-foreground/85">{a}</dd>
          </div>
        ))}
      </dl>

      <h2>Find international dividend payers in DiviDrip</h2>
      <p>
        Open <Link to="/dashboard">DiviDrip</Link> and search ADR tickers like{' '}
        <strong>UL</strong>, <strong>BTI</strong>, <strong>NVS</strong>,{' '}
        <strong>NVO</strong>, <strong>ASML</strong>, <strong>TSM</strong>,{' '}
        <strong>TD</strong>, <strong>RY</strong>, <strong>SAP</strong>,{' '}
        <strong>BHP</strong>. The Stock Modal header carries an <strong>ADR</strong>{' '}
        badge for foreign listings, and the Dividend Info tab opens an ADR
        explainer card with the country-specific withholding context. Combine
        that with the Triangle score to find international compounders worth
        the tax plumbing.
      </p>
      <p className="text-xs text-muted-foreground italic mt-6">
        Not tax advice. Treaty rates and withholding rules change; always confirm
        with a tax professional and your current 1099-DIV before making
        account-placement decisions. Rates reflect the 2026 tax year as of
        publication.
      </p>
    </LearnPage>
  );
}
